import { useEffect, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useAuthStore } from "./store";
import type { UserRole } from "@/types";

const ROLE_LEVELS: Record<UserRole, number> = {
  viewer: 0,
  user: 1,
  manager: 2,
  admin: 3,
};

interface RoleGuardProps {
  minRole: UserRole;
  children: ReactNode;
  redirectTo?: string;
}

export function RoleGuard({ minRole, children, redirectTo = "/simulators" }: RoleGuardProps) {
  const navigate = useNavigate();
  const { user, isLoading, _hasHydrated } = useAuthStore();

  const hasAccess =
    user != null && ROLE_LEVELS[user.role] >= ROLE_LEVELS[minRole];

  useEffect(() => {
    if (!_hasHydrated || isLoading) return;
    if (user && !hasAccess) {
      navigate({ to: redirectTo, replace: true });
    }
  }, [user, hasAccess, isLoading, _hasHydrated, navigate, redirectTo]);

  if (!_hasHydrated || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) return null;

  return <>{children}</>;
}

interface RolesGuardProps {
  allow: UserRole[];
  children: ReactNode;
  redirectTo?: string;
}

/**
 * Restricts access to an explicit list of roles. Useful when the allowed roles
 * are not contiguous in the role hierarchy (e.g. allow viewer/manager/admin but
 * exclude the standard `user` role).
 */
export function RolesGuard({ allow, children, redirectTo = "/simulators" }: RolesGuardProps) {
  const navigate = useNavigate();
  const { user, isLoading, _hasHydrated } = useAuthStore();

  const hasAccess = user != null && allow.includes(user.role);

  useEffect(() => {
    if (!_hasHydrated || isLoading) return;
    if (user && !hasAccess) {
      navigate({ to: redirectTo, replace: true });
    }
  }, [user, hasAccess, isLoading, _hasHydrated, navigate, redirectTo]);

  if (!_hasHydrated || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) return null;

  return <>{children}</>;
}
